/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CherreDemographicsSummary } from '../models/CherreDemographicsSummary';
import type { MarketComparablePayload } from '../models/MarketComparablePayload';
import type { MarketComparableResponse } from '../models/MarketComparableResponse';
import type { MarketSearchFilters } from '../models/MarketSearchFilters';
import type { MarketSearchResponse } from '../models/MarketSearchResponse';
import type { MarketStatsQuery } from '../models/MarketStatsQuery';
import type { MarketStatsResponse } from '../models/MarketStatsResponse';
import type { PropertySearchResponse } from '../models/PropertySearchResponse';
import type { SimpleMarketFilters } from '../models/SimpleMarketFilters';
import type { TaxHistoryResponse } from '../models/TaxHistoryResponse';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class MarketService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * Find census tracts that are similar to the given census tract in terms of demographics
     * and housing characteristics.
     * @param requestBody
     * @returns MarketComparableResponse The market comparables.
     * @throws ApiError
     */
    public marketComparables(
        requestBody: MarketComparablePayload,
    ): CancelablePromise<Array<MarketComparableResponse>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/market/comparables',
            body: requestBody,
            mediaType: 'application/json',
        });
    }
    /**
     * Get market statistics for given property characteristics (number of units, year built, etc.)
     * Returns the number of properties, the number of units, the average unit mix, and amenity frequencies.
     * @param requestBody Property characteristics.
     * @returns MarketStatsResponse The market statistics.
     * @throws ApiError
     */
    public marketStatistics(
        requestBody: {
            subject: MarketStatsQuery;
        },
    ): CancelablePromise<MarketStatsResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/market/statistics',
            body: requestBody,
            mediaType: 'application/json',
        });
    }
    /**
     * Performs a property search using geometric shapes and advanced filtering criteria.
     * This endpoint enables flexible market analysis by finding properties within custom-defined boundaries
     * and applying multi-dimensional filters to refine results based on property characteristics.
     *
     * NOTE: This new endpoint needs to be activated for your account and is charged at a higher rate. Please contact us to activate.
     *
     * ### Core Functionality
     *
     * The search combines geographic boundaries (defined by coordinate polygons) with property-specific filters
     * such as construction year, building size, unit mix characteristics, and property type classifications.
     * This allows for precise market analysis within irregular geographic areas like neighborhoods, custom
     * market areas, or specific zones of interest.
     *
     * ### Geographic Boundaries
     *
     * **Coordinate Format**: Geographic boundaries are defined using GeoJSON-style coordinate arrays.
     * The format follows the pattern: `number[][][][]` where:
     * - **Outer array**: Contains multiple polygons (for complex shapes with holes or multiple areas)
     * - **Second level**: Contains individual polygons
     * - **Third level**: Contains coordinate rings (first ring is exterior, subsequent rings are holes)
     * - **Inner arrays**: Individual coordinate pairs as `[longitude, latitude]`
     *
     * **Example coordinate structures**:
     * ```javascript
     * // Simple rectangular area in Manhattan
     * [[[[-73.99, 40.75], [-73.98, 40.75], [-73.98, 40.76], [-73.99, 40.76], [-73.99, 40.75]]]]
     *
     * // Complex shape with a hole (e.g., excluding a park)
     * [[[
         * [[-73.99, 40.75], [-73.97, 40.75], [-73.97, 40.77], [-73.99, 40.77], [-73.99, 40.75]], // Outer boundary
         * [[-73.985, 40.755], [-73.975, 40.755], [-73.975, 40.765], [-73.985, 40.765], [-73.985, 40.755]] // Hole
         * ]]]
         * ```
         *
         * ### Filtering System
         *
         * **Range Filters**: Most numeric filters use a standardized range format with three properties:
         * - `min`: Minimum value (inclusive). Use `null` to omit minimum constraint
         * - `max`: Maximum value (inclusive). Use `null` to omit maximum constraint
         * - `allow_null`: Whether to include properties with missing data for this field (default: `false`)
         *
         * **Property Type Filters**: Boolean filters for different property classifications:
         * - `is_student`: Student housing complexes (detected via amenities, naming patterns, pricing)
         * - `is_senior`: Senior living communities
         * - `is_affordable`: Properties with affordable housing units (uses LIHTC database and other sources)
         *
         * **Unit Mix Filters**: Advanced filtering based on unit composition and pricing:
         * - Filter by unit counts, square footage, rent levels, and rent per square foot
         * - Available for overall property and by bedroom count (studio through 4-bedroom)
         * - Uses historical market data to calculate averages
         *
         * ### Performance & Limitations
         *
         * **Result Limits**: Each request is capped at **2,500 properties** to ensure optimal performance.
         * If you hit this limit, consider:
         * - Narrowing your geographic boundary
         * - Adding more restrictive filters (year built, unit count, etc.)
         * - Splitting large areas into multiple searches
         *
         * **Geographic Scope**: Searches are optimized for metropolitan areas. Very large rural areas
         * may return fewer relevant results due to lower property density.
         *
         * **Data Freshness**: Property data is continuously updated, but some filters (especially unit mix)
         * rely on historical market data that may have varying collection periods across markets.
         *
         * ### Usage Recommendations
         *
         * **For Market Analysis**:
         * - Use generous geographic boundaries initially, then refine with property filters
         * - Combine year built and unit count filters to focus on comparable property classes
         * - Leverage unit mix filters to find properties with similar tenant profiles
         *
         * **For Competitive Analysis**:
         * - Start with broader search area around subject property
         * - Use property type filters to exclude non-competing assets
         * - Apply unit mix filters to match target demographics
         *
         * **For Site Selection**:
         * - Define custom geographic boundaries around areas of interest
         * - Use year built filters to identify redevelopment opportunities or new construction
         * - Apply unit mix filters to understand existing supply characteristics
         *
         * Each request is limited to a maximum of 2,500 results. If you hit this limit, consider refining your filters
         * to narrow the search and reduce the result count.
         * @param requestBody Market filter criteria including geographic boundaries and property filters
         * @returns any List of properties matching the specified search criteria, limited to 2,500 results
         * @throws ApiError
         */
        public marketSearch(
            requestBody: SimpleMarketFilters,
        ): CancelablePromise<{
            properties: Array<PropertySearchResponse>;
        }> {
            return this.httpRequest.request({
                method: 'POST',
                url: '/market/search',
                body: requestBody,
                mediaType: 'application/json',
            });
        }
        /**
         * Search for markets by name, city, state, or tags. Returns names as well as a geojson object of the coordinates of the market.
         * This endpoint is free to use and enabled on demand. Please contact us to activate.
         * @param q The search query string. Can include a market name, city, state, or tag.
         * @returns MarketSearchResponse List of markets matching the specified search criteria. The list is limited to 7 results.
         * @throws ApiError
         */
        public shapeSearch(
            q: string,
        ): CancelablePromise<Array<MarketSearchResponse>> {
            return this.httpRequest.request({
                method: 'GET',
                url: '/market/shape_search',
                query: {
                    'q': q,
                },
            });
        }
        /**
         * @param requestBody
         * @returns any Ok
         * @throws ApiError
         */
        public marketDemographics(
            requestBody: MarketSearchFilters,
        ): CancelablePromise<{
            demographics: CherreDemographicsSummary;
        }> {
            return this.httpRequest.request({
                method: 'POST',
                url: '/market/demographics',
                body: requestBody,
                mediaType: 'application/json',
            });
        }
        /**
         * Get tax assessor parcel IDs, recorder summary, assessor history, and AVM data
         * for a given address context. Returns structured response grouped by table.
         * @param requestBody
         * @returns TaxHistoryResponse Ok
         * @throws ApiError
         */
        public taxHistory(
            requestBody: {
                lon: number;
                lat: number;
                zip_code: string;
                state: string;
                city: string;
                address: string;
                id: string;
            },
        ): CancelablePromise<TaxHistoryResponse> {
            return this.httpRequest.request({
                method: 'POST',
                url: '/market/tax_history',
                body: requestBody,
                mediaType: 'application/json',
            });
        }
    }
